

  import Vue from 'vue'
  import Component from "vue-class-component";

  import {Prop, Watch} from "vue-property-decorator";
  import {IServiceProvider} from "@/model/Service/serviceProviderInterfaces";
  import {IErrors} from "@/model/IErrors";
  import {namespace} from "vuex-class";
  import {IShortChannel} from "@/model/shortInterfaces";
  import {IOnOffHandlers} from "bonomo/dist";
  import ChannelChip from "@/components/shared/chips/ChannelChip.vue";

  const serviceProviderModule = namespace("serviceProviderModule");

  @Component({
    components: {ChannelChip}
  })
  export default class SPChannels extends Vue {

    @serviceProviderModule.Getter('serviceProvidersChannels')
    protected channels!: IShortChannel[];
    @Prop()
    protected provider!: IServiceProvider;
    @Prop()
    protected errors!: IErrors;
    @Prop()
    protected viewing!: boolean;
    @Prop()
    protected canEdit!: boolean;
    @Prop()
    protected editing!: boolean;
    @Prop()
    protected disabled!: boolean;

    @Watch('provider.id')
    spIdChanged() {
      this.loadData();
    }
    @Watch('provider.channel_ids')
    channelIdsChanged() {
      this.selectedChannelId = this.provider.channel_ids.length ? this.provider.channel_ids[0] : null;
    }

    protected selectedChannelId: number|null = null;
    protected channelError: string[] = [];
    protected loading: boolean = false;
    protected submitting: boolean = false;
    private subscribes: IOnOffHandlers = {};

    created() {
      this.selectedChannelId = this.provider.channel_ids.length ? this.provider.channel_ids[0] : null;
      this.loadData();
      this.subscribes = this.$ons({
        'login.OK': this.loadData,
        'backend.call.OK.serviceProvidersChannelsLoaded': this.onChannelsLoaded,
      });
    }

    beforeDestroy() {
      this.$offs(this.subscribes);
    }

    protected getChannel(channelId: number): IShortChannel|undefined {
      return this.channels.find(each => each.id == channelId);
    }

    protected get selectedChannel(): IShortChannel|undefined {
      return this.selectedChannelId ? this.getChannel(this.selectedChannelId) : undefined;
    }

    protected async onChannelSelected(newChannelId: number) {
      const previousChannelId = this.selectedChannelId;
      if (newChannelId === previousChannelId) {
        return;
      }

      this.submitting = true;
      this.channelError = [];

      try {
        if (previousChannelId) {
          await this.dispatchAndWait(false, previousChannelId);
        }
        await this.dispatchAndWait(true, newChannelId);
        this.selectedChannelId = newChannelId;
        this.$uiBus.emit('success', this.$t('common.saved'));
      } catch (e) {
        const errors = e as IErrors;
        this.channelError = (errors && errors._) || [];
      } finally {
        this.submitting = false;
      }
    }

    private get spHasChannels(): boolean {
      return !!this.provider.channel_ids.length;
    }

    private loadData() {
      if (!this.provider || !this.provider.id) {
        return;
      }
      this.loading = true;
      this.$store.dispatch('serviceProviderModule/fetchServiceProvidersChannels', this.provider.id);
    }

    private onChannelsLoaded() {
      this.loading = false;
    }

    // connectChannel resolves its dispatch before the HTTP call finishes, so we can't just await it:
    // wait for the specific backend.call.OK/KO event instead, matching the pattern used elsewhere in this app.
    private dispatchAndWait(connect: boolean, channelId: number): Promise<void> {
      this.$store.dispatch('serviceProviderModule/connectChannel', {
        serviceProviderId: this.provider.id,
        channelId,
        connect,
      });

      return new Promise((resolve, reject) => {
        const handlers: IOnOffHandlers = {
          'backend.call.OK.serviceProviderLoaded': () => {
            this.$offs(handlers);
            resolve();
          },
          'backend.call.KO': (mutationName: string, errors: IErrors) => {
            if (mutationName === 'serviceProviderLoaded') {
              this.$offs(handlers);
              reject(errors);
            }
          },
        };
        this.$ons(handlers);
      });
    }

  }

